import React from "react";

interface ErrorStateProps {
  error: Error | null;
  onRetry?: () => void;
}

export const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry }) => {
  const isRateLimited = error?.message?.includes("429") || error?.message?.includes("rate limit");

  return (
    <div
      className="bg-white rounded-lg border border-red-200 p-8 text-center"
      data-testid="error-state"
      role="alert"
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
        <svg
          className="w-6 h-6 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>

      <h3 className="text-lg font-semibold text-neutral-900 mb-2">
        {isRateLimited ? "Przekroczono limit" : "Błąd ładowania danych"}
      </h3>

      <p className="text-neutral-600 mb-4 max-w-md mx-auto">
        {isRateLimited
          ? "Wysłano zbyt wiele żądań. Poczekaj chwilę przed ponowną próbą."
          : error?.message || "Wystąpił nieoczekiwany błąd podczas ładowania danych."}
      </p>

      {onRetry && (
        <button
          onClick={onRetry}
          aria-label="Ponów ładowanie danych"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Spróbuj ponownie
        </button>
      )}
    </div>
  );
};

export const EmptyState: React.FC = () => {
  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-8 text-center"
      data-testid="outbox-empty-state"
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-neutral-100 mb-4">
        <svg
          className="w-6 h-6 text-neutral-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>

      <h3 className="text-lg font-semibold text-neutral-900 mb-2">
        Brak eventów w outbox
      </h3>

      <p className="text-neutral-600 max-w-md mx-auto">
        System działa normalnie — brak eventów oczekujących na przetworzenie.
      </p>
    </div>
  );
};

interface AccessDeniedStateProps {
  message?: string;
}

export const AccessDeniedState: React.FC<AccessDeniedStateProps> = ({
  message = "Nie masz uprawnień do tej strony.",
}) => {
  return (
    <div
      className="bg-white rounded-lg border border-amber-200 p-8 text-center"
      data-testid="access-denied-state"
      role="alert"
    >
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-4">
        <svg
          className="w-6 h-6 text-amber-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      </div>

      <h3 className="text-lg font-semibold text-neutral-900 mb-2">
        Brak dostępu
      </h3>

      <p className="text-neutral-600 max-w-md mx-auto">{message}</p>
    </div>
  );
};

const stateComponents = { ErrorState, EmptyState, AccessDeniedState };
export default stateComponents;
